import { useEffect, useState } from "react";
import { Bot } from "lucide-react";
import { PassportIcon, StampIcon, SchengenVisaIcon } from "@/components/portal/OfficialIcons";

/**
 * PortalValueSection
 * Premium "Why this portal" section. Renders WITHOUT a distinct background
 * so it visually flows as one continuous page with the CTA above. The plane
 * orbit + faint passport/stamp marks are rendered globally by
 * <LandingBackdrop /> in PortalLanding (so they span CTA + cards together).
 *
 * Icons:
 *   - Cards 1-3 use a closed-passport SVG that flips OPEN on hover.
 *   - Card 4 (Fahad) keeps a Lucide Bot icon as it is the AI assistant.
 */
const TEXTS = {
  ar: {
    title: "بوابة مصممة لخدمتك وحفظ وقتك في إجراءات ما قبل الحصول على التأشيرة",
    disclaimer: "flow",
    flow_step1: "نستلم ملفك ونجهّزه",
    flow_step2: "نرسله للجهة المختصة",
    flow_step3: "تُصدر التأشيرة",
    sub: "متابعة طلبك، معرفة موعدك، والاطلاع على آخر التحديثات في مكان واحد.",
    eyebrow: "VTA",
    cards: [
      {
        t: "طلبك يبدأ بوضوح",
        d: "ابدأ معاملتك من البوابة، وارفع بياناتك ومستنداتك المطلوبة في مكان واحد بطريقة منظمة وسهلة.",
      },
      {
        t: "متطلباتك حسب الجهة",
        d: "نساعدك على معرفة البيانات والمتطلبات الأساسية حسب نوع التأشيرة واشتراطات الجهة المعنية بكل دولة.",
      },
      {
        t: "تحديثات معاملتك في حسابك",
        d: "بعد مراجعة الطلب، تظهر لك حالة معاملتك والتحديثات المهمة داخل البوابة، حتى تكون الصورة واضحة أمامك.",
      },
      {
        t: "فهد، مساعدك الذكي",
        d: "وفرنا لك فهد لمساعدتك في الأسئلة الشائعة ومعرفة حالة معاملتك بطريقة أسرع وأسهل من داخل البوابة.",
      },
    ],
  },
  en: {
    title: "A portal designed to serve you and save your time in pre-visa procedures",
    disclaimer: "flow",
    flow_step1: "We prepare your file",
    flow_step2: "We send it to the authority",
    flow_step3: "Your visa is issued",
    sub: "Track your case, view your appointment, and follow updates — all in one place.",
    eyebrow: "VTA",
    cards: [
      {
        t: "Your application starts clearly",
        d: "Start your case through the portal and upload your information and required documents in one organized place — simple and easy to follow.",
      },
      {
        t: "Requirements by authority",
        d: "We help you understand the core data and requirements based on the visa type and the specific authority's conditions for each country.",
      },
      {
        t: "Case updates in your account",
        d: "Once your application is reviewed, your case status and important updates appear inside the portal — so the full picture stays clear for you.",
      },
      {
        t: "Fahad, your smart assistant",
        d: "We've equipped Fahad to help with frequently asked questions and check your case status faster and easier — right from the portal.",
      },
    ],
  },
  ur: {
    title: "آپ کی خدمت اور وقت کی بچت کے لیے بنایا گیا پورٹل — ویزا سے پہلے کے اقدامات کے لیے",
    disclaimer: "flow",
    flow_step1: "ہم آپ کی فائل تیار کرتے ہیں",
    flow_step2: "متعلقہ ادارے کو بھیجتے ہیں",
    flow_step3: "ویزا جاری ہو جاتا ہے",
    sub: "اپنی درخواست کی پیروی، اپائنٹمنٹ کی تفصیل اور اپڈیٹس — سب ایک ہی جگہ۔",
    eyebrow: "VTA",
    cards: [
      {
        t: "آپ کی درخواست واضح آغاز کے ساتھ",
        d: "پورٹل سے اپنی درخواست شروع کریں اور اپنی معلومات اور مطلوبہ دستاویزات کو ایک ہی منظم اور آسان جگہ پر اپ لوڈ کریں۔",
      },
      {
        t: "ادارے کے مطابق ضروریات",
        d: "ہم آپ کو ویزا کی قسم اور ہر ملک کے متعلقہ ادارے کی شرائط کے مطابق بنیادی معلومات اور ضروریات سمجھنے میں مدد دیتے ہیں۔",
      },
      {
        t: "آپ کے اکاؤنٹ میں درخواست کی اپڈیٹس",
        d: "درخواست کے جائزے کے بعد، آپ کی درخواست کی حالت اور اہم اپڈیٹس پورٹل کے اندر ظاہر ہو جاتی ہیں، تاکہ مکمل تصویر آپ کے سامنے واضح رہے۔",
      },
      {
        t: "فہد، آپ کا اسمارٹ معاون",
        d: "ہم نے فہد کو تیار کیا ہے تاکہ وہ عام سوالات میں آپ کی مدد کرے اور پورٹل کے اندر سے آپ کی درخواست کی حالت کو زیادہ تیز اور آسان طریقے سے جان سکیں۔",
      },
    ],
  },
};

export default function PortalValueSection({ theme, dir, lang }) {
  const t = TEXTS[lang] || TEXTS.ar;
  const isDark = theme.name === "dark";

  const [vw, setVw] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isMobile = vw < 720;
  const isTablet = vw >= 720 && vw < 1080;
  const gridCols = isMobile ? "1fr" : isTablet ? "1fr 1fr" : "1fr 1fr 1fr 1fr";

  const titleColor = isDark ? "#f3eccb" : "#1f1d18";
  const subColor = isDark ? "#c9c4b1" : "#4a4639";
  const cardBg = isDark
    ? "linear-gradient(160deg, rgba(28,24,16,0.78) 0%, rgba(20,18,12,0.78) 100%)"
    : "linear-gradient(160deg, rgba(255,255,255,0.92) 0%, rgba(251,247,235,0.92) 100%)";

  return (
    <section
      dir={dir}
      data-testid="portal-value-section"
      style={{
        // Continuous page — no separate background, no border break.
        // Stretches edge-to-edge by breaking out of PortalLanding's 24px padding.
        width: "100vw",
        position: "relative",
        marginInlineStart: "calc(50% - 50vw)",
        marginInlineEnd: "calc(50% - 50vw)",
        marginTop: "8px",
        padding: isMobile ? "8px 20px 56px" : "16px 32px 80px",
        background: "transparent",
        zIndex: 2,
      }}
    >
      <div style={{ maxWidth: "1180px", margin: "0 auto", textAlign: "center", position: "relative", zIndex: 2 }}>
        <h2
          style={{
            fontSize: isMobile ? "26px" : "38px",
            fontWeight: 900,
            color: titleColor,
            margin: "0 0 12px",
            lineHeight: 1.3,
          }}
          data-testid="value-section-title"
        >
          {t.title}
        </h2>

        {t.disclaimer && (
          <WorkflowStrip theme={theme} dir={dir} isMobile={isMobile} />
        )}

        <p
          style={{
            fontSize: isMobile ? "13px" : "15px",
            color: subColor,
            margin: "0 auto 48px",
            maxWidth: "none",
            lineHeight: 1.6,
            whiteSpace: isMobile ? "normal" : "nowrap",
          }}
          data-testid="value-section-subtitle"
        >
          {t.sub}
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: gridCols,
            gap: isMobile ? "16px" : "22px",
            textAlign: dir === "rtl" ? "right" : "left",
          }}
        >
          {t.cards.map((card, i) => (
            <ValueCard
              key={i}
              index={i}
              card={card}
              theme={theme}
              titleColor={titleColor}
              subColor={subColor}
              cardBg={cardBg}
              isDark={isDark}
              isMobile={isMobile}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ Card ----------------------------- */
function ValueCard({ index, card, theme, titleColor, subColor, cardBg, isDark, isMobile }) {
  const [hover, setHover] = useState(false);
  // 0 → Passport (closed → opens on hover) — "Your application starts clearly"
  // 1 → Official stamps — "Requirements are clearer"
  // 2 → Schengen visa sticker — "Your appointment, tracked"
  // 3 → Bot (Fahad AI assistant)
  return (
    <div
      data-testid={`value-card-${index + 1}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: cardBg,
        backdropFilter: "blur(6px)",
        border: `1px solid ${theme.borderAccent}`,
        borderRadius: "18px",
        padding: isMobile ? "24px 22px" : "30px 26px",
        boxShadow: isDark
          ? "0 14px 40px rgba(0,0,0,0.35)"
          : "0 14px 40px rgba(120,90,30,0.10)",
        position: "relative",
        transition: "transform 220ms ease, box-shadow 220ms ease",
        transform: hover ? "translateY(-4px)" : "translateY(0)",
      }}
    >
      <div
        style={{
          width: "64px",
          height: "64px",
          borderRadius: "14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: `1.2px solid ${theme.borderAccent}`,
          background: isDark ? "rgba(212,175,55,0.07)" : "rgba(255,255,255,0.85)",
          color: theme.gold,
          marginBottom: "18px",
        }}
      >
        {index === 0 && <PassportIcon open={hover} color={theme.gold} size={40} />}
        {index === 1 && <StampIcon color={theme.gold} size={40} />}
        {index === 2 && <SchengenVisaIcon color={theme.gold} size={40} />}
        {index === 3 && <Bot size={32} strokeWidth={1.4} />}
      </div>

      <h3 style={{ fontSize: "17px", fontWeight: 800, color: titleColor, margin: "0 0 10px", lineHeight: 1.4 }}>
        {card.t}
      </h3>
      <p style={{ fontSize: "13.5px", color: subColor, margin: 0, lineHeight: 1.8 }}>
        {card.d}
      </p>

      <div
        style={{
          position: "absolute",
          insetInlineStart: 0,
          bottom: 0,
          width: "44px",
          height: "3px",
          borderRadius: "0 3px 0 3px",
          background: theme.goldGradient,
          opacity: 0.85,
        }}
      />
    </div>
  );
}

/* ──────────── Workflow Flow — compact horizontal storyline ────────────
 * A small, horizontal pill that tells the story:
 *   [ Counter "+1200 عميل" ] | [ Office → Embassy → Airport → Happy Client ]
 *
 * A single glowing dot travels along the row. When it arrives at the
 * EMBASSY and at the AIRPORT, country flags burst from below the node,
 * scale up, then fade away. At the CLIENT a celebratory ✓ pops with a
 * sparkle. Cycle repeats forever.
 *
 * Flags shown: 🇷🇺 Russia · 🇬🇧 Britain · 🇨🇦 Canada · 🇪🇺 Schengen · 🇺🇸 USA
 */
function WorkflowStrip({ theme, dir, isMobile }) {
  const isDark = theme.name === "dark";
  const isRTL = dir === "rtl";
  const gold = theme.gold;
  const goldSoft = isDark ? "rgba(212,175,55,0.45)" : "rgba(166,124,26,0.55)";
  const goldFaint = isDark ? "rgba(212,175,55,0.20)" : "rgba(166,124,26,0.25)";

  // Tagline content (replaces the numeric counter — a step closer to your journey)
  const taglineByDir = {
    ar: { line1: "خطوة أقرب", line2: "نحو رحلتك" },
    en: { line1: "A step closer", line2: "to your journey" },
    ur: { line1: "ایک قدم قریب", line2: "آپ کے سفر کی طرف" },
  };
  const langKey = isRTL ? (dir === "rtl" ? "ar" : "ur") : "en";
  // Use stored html dir as primary signal; fall back to ar when unknown RTL
  const htmlLang = typeof document !== "undefined" ? document.documentElement.lang : "";
  const resolvedLang = ["ar", "en", "ur"].includes(htmlLang) ? htmlLang : (isRTL ? "ar" : "en");
  const tagline = taglineByDir[resolvedLang] || taglineByDir.ar;

  // Tagline label (no separate label below; the tagline carries the message)
  const counterLabel = "";

  // Workflow SVG dimensions — compact horizontal strip
  const W = isMobile ? 240 : 440;
  const H = isMobile ? 56 : 64;
  const cy = H / 2 + 2; // a tiny bit below center to leave space above for flags

  // 4 nodes spaced evenly along the strip
  // We compute positions relative to LTR order (office → embassy → airport → client),
  // then mirror horizontally when RTL so the office sits on the right.
  const nodePadX = isMobile ? 16 : 22;
  const nodeStep = (W - nodePadX * 2) / 3;
  const posLTR = [0, 1, 2, 3].map((i) => nodePadX + i * nodeStep);
  const positions = isRTL ? [...posLTR].reverse() : posLTR;
  const [xOffice, xEmbassy, xAirport, xClient] = positions;

  // Path the dot travels (straight line with subtle wave between nodes)
  const path = isRTL
    ? `M ${xOffice} ${cy} Q ${(xOffice + xEmbassy) / 2} ${cy - 8} ${xEmbassy} ${cy} Q ${(xEmbassy + xAirport) / 2} ${cy + 6} ${xAirport} ${cy} Q ${(xAirport + xClient) / 2} ${cy - 6} ${xClient} ${cy}`
    : `M ${xOffice} ${cy} Q ${(xOffice + xEmbassy) / 2} ${cy - 8} ${xEmbassy} ${cy} Q ${(xEmbassy + xAirport) / 2} ${cy + 6} ${xAirport} ${cy} Q ${(xAirport + xClient) / 2} ${cy - 6} ${xClient} ${cy}`;

  // Flag burst params
  const burstNodes = [
    { x: xEmbassy, delayPct: 25 }, // burst appears when dot reaches embassy
    { x: xAirport, delayPct: 58 }, // burst appears when dot reaches airport
  ];
  const flagCodes = ["ru", "gb", "ca", "eu", "us"];
  const flagW = isMobile ? 16 : 20;
  const flagH = isMobile ? 11 : 13;
  const burstRadius = isMobile ? 22 : 30;
  // Flags fan upward across an arc from -140° to -40°
  const flagSpec = flagCodes.map((code, i) => {
    const t = flagCodes.length === 1 ? 0.5 : i / (flagCodes.length - 1);
    const angle = (-140 + t * 100) * (Math.PI / 180);
    return { code, dx: Math.cos(angle) * burstRadius, dy: Math.sin(angle) * burstRadius, idx: i };
  });

  return (
    <div
      data-testid="value-section-flow"
      style={{
        display: "inline-flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: isMobile ? 10 : 16,
        margin: "0 auto 22px",
        padding: isMobile ? "8px 12px" : "10px 16px",
        borderRadius: "999px",
        border: `1px solid ${theme.borderAccent}`,
        background: isDark
          ? "linear-gradient(135deg, rgba(28,24,14,0.6) 0%, rgba(18,16,10,0.5) 100%)"
          : "linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(251,247,235,0.7) 100%)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        boxShadow: isDark
          ? "0 10px 28px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.04)"
          : "0 10px 28px rgba(166,124,26,0.10), inset 0 1px 0 rgba(255,255,255,0.7)",
      }}
    >
      {/* TAGLINE chip — "A step closer to your journey" */}
      <div
        data-testid="value-section-counter"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minWidth: isMobile ? 96 : 132,
          lineHeight: 1.15,
        }}
      >
        <div
          style={{
            fontSize: isMobile ? "13px" : "16px",
            fontWeight: 800,
            color: gold,
            letterSpacing: "0.1px",
            textShadow: isDark ? `0 0 14px ${gold}33` : "none",
            whiteSpace: "nowrap",
          }}
          className="vta-tagline-1"
        >
          {tagline.line1}
        </div>
        <div
          style={{
            fontSize: isMobile ? "11px" : "13px",
            fontWeight: 600,
            color: isDark ? "rgba(255,255,255,0.78)" : "rgba(40,30,8,0.78)",
            marginTop: 3,
            whiteSpace: "nowrap",
            letterSpacing: "0.1px",
          }}
          className="vta-tagline-2"
        >
          {tagline.line2}
        </div>
      </div>

      {/* Vertical separator */}
      <div
        aria-hidden
        style={{
          width: 1,
          height: isMobile ? 36 : 44,
          background: `linear-gradient(to bottom, transparent, ${goldSoft}, transparent)`,
          flexShrink: 0,
        }}
      />

      {/* WORKFLOW SVG */}
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} fill="none" aria-hidden>
        <defs>
          <radialGradient id="vta-glow-sm" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={gold} stopOpacity="0.65" />
            <stop offset="100%" stopColor={gold} stopOpacity="0" />
          </radialGradient>
          <radialGradient id="vta-dot-halo" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={gold} stopOpacity="0.85" />
            <stop offset="100%" stopColor={gold} stopOpacity="0" />
          </radialGradient>
          <clipPath id="vta-flag-clip-sm" clipPathUnits="objectBoundingBox">
            <rect x="0" y="0" width="1" height="1" rx="0.12" ry="0.12" />
          </clipPath>
        </defs>

        {/* Wavy reference path */}
        <path d={path} stroke={goldFaint} strokeWidth="1.1" strokeDasharray="2 3" fill="none" />

        {/* Nodes */}
        <NodeIcon kind="office"  x={xOffice}  y={cy} gold={gold} isDark={isDark} />
        <NodeIcon kind="embassy" x={xEmbassy} y={cy} gold={gold} isDark={isDark} pulseClass="vta-node-embassy" />
        <NodeIcon kind="airport" x={xAirport} y={cy} gold={gold} isDark={isDark} pulseClass="vta-node-airport" />
        <NodeIcon kind="client"  x={xClient}  y={cy} gold={gold} isDark={isDark} pulseClass="vta-node-client" />

        {/* Flag bursts: at embassy + at airport */}
        {burstNodes.map((node, ni) => (
          <g key={ni}>
            {flagSpec.map((f) => (
              <g
                key={`${ni}-${f.code}`}
                transform={`translate(${node.x + f.dx - flagW / 2}, ${cy + f.dy - flagH / 2})`}
              >
                <g
                  className={`vta-flag-burst burst-${ni}`}
                  style={{
                    transformBox: "fill-box",
                    transformOrigin: "center center",
                    animationDelay: `${f.idx * 80}ms`,
                  }}
                >
                  <FlagGlyph code={f.code} W={flagW} H={flagH} />
                  <rect
                    x="0" y="0" width={flagW} height={flagH}
                    rx="1.6" ry="1.6"
                    stroke={isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.3)"}
                    strokeWidth="0.6"
                    fill="none"
                  />
                </g>
              </g>
            ))}
          </g>
        ))}

        {/* Client celebration sparkle ring */}
        <g
          className="vta-client-spark"
          style={{ transformBox: "fill-box", transformOrigin: `${xClient}px ${cy}px` }}
        >
          {[0, 60, 120, 180, 240, 300].map((deg, i) => {
            const a = (deg * Math.PI) / 180;
            const r = 14;
            return (
              <circle
                key={i}
                cx={xClient + Math.cos(a) * r}
                cy={cy + Math.sin(a) * r}
                r="1.4"
                fill={gold}
              />
            );
          })}
        </g>

        {/* Travelling glow dot */}
        <circle r={isMobile ? 5 : 6} fill="url(#vta-dot-halo)" className="vta-flow-dot-halo">
          <animateMotion dur="8s" repeatCount="indefinite" path={path} keyTimes="0;0.85;1" keyPoints="0;1;1" calcMode="linear" />
        </circle>
        <circle r={isMobile ? 2.6 : 3.2} fill={gold} className="vta-flow-dot">
          <animateMotion dur="8s" repeatCount="indefinite" path={path} keyTimes="0;0.85;1" keyPoints="0;1;1" calcMode="linear" />
        </circle>
      </svg>

      <style>{`
        /* Node pulse — embassy at 22-28%, airport at 55-62%, client at 82-92% */
        .vta-node-embassy {
          transform-box: fill-box; transform-origin: center;
          animation: vta-node-pulse-embassy 8s ease-in-out infinite;
        }
        .vta-node-airport {
          transform-box: fill-box; transform-origin: center;
          animation: vta-node-pulse-airport 8s ease-in-out infinite;
        }
        .vta-node-client {
          transform-box: fill-box; transform-origin: center;
          animation: vta-node-pulse-client 8s ease-in-out infinite;
        }
        @keyframes vta-node-pulse-embassy {
          0%, 18%, 100% { transform: scale(1); filter: none; }
          25%, 30%      { transform: scale(1.18); filter: drop-shadow(0 0 6px ${gold}); }
        }
        @keyframes vta-node-pulse-airport {
          0%, 52%, 100% { transform: scale(1); filter: none; }
          58%, 64%      { transform: scale(1.18); filter: drop-shadow(0 0 6px ${gold}); }
        }
        @keyframes vta-node-pulse-client {
          0%, 80%, 100% { transform: scale(1); filter: none; }
          88%, 94%      { transform: scale(1.22); filter: drop-shadow(0 0 8px ${gold}); }
        }

        /* Flag burst: emerge → hover → fade. Two independent timelines for each node. */
        .vta-flag-burst {
          opacity: 0;
        }
        .vta-flag-burst.burst-0 {
          animation: vta-burst-embassy 8s cubic-bezier(0.34, 1.56, 0.64, 1) infinite;
        }
        .vta-flag-burst.burst-1 {
          animation: vta-burst-airport 8s cubic-bezier(0.34, 1.56, 0.64, 1) infinite;
        }
        @keyframes vta-burst-embassy {
          0%, 20%   { opacity: 0; transform: scale(0.2) translate(0, 4px); }
          26%, 30%  { opacity: 1; transform: scale(1)   translate(0, 0); }
          40%       { opacity: 1; transform: scale(1)   translate(0, -2px); }
          46%, 100% { opacity: 0; transform: scale(0.85) translate(0, -6px); }
        }
        @keyframes vta-burst-airport {
          0%, 54%   { opacity: 0; transform: scale(0.2) translate(0, 4px); }
          60%, 64%  { opacity: 1; transform: scale(1)   translate(0, 0); }
          74%       { opacity: 1; transform: scale(1)   translate(0, -2px); }
          80%, 100% { opacity: 0; transform: scale(0.85) translate(0, -6px); }
        }

        /* Client sparkles burst at the end */
        .vta-client-spark {
          opacity: 0;
          animation: vta-spark 8s ease-out infinite;
        }
        @keyframes vta-spark {
          0%, 82%   { opacity: 0; transform: scale(0.4); }
          88%, 94%  { opacity: 1; transform: scale(1); }
          100%      { opacity: 0; transform: scale(1.3); }
        }

        @media (prefers-reduced-motion: reduce) {
          .vta-node-embassy, .vta-node-airport, .vta-node-client,
          .vta-flag-burst, .vta-client-spark, .vta-flow-dot, .vta-flow-dot-halo { animation: none; }
          .vta-flow-dot, .vta-flow-dot-halo { display: none; }
          .vta-flag-burst { opacity: 1; transform: scale(1); }
          .vta-client-spark { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

/* ─────────────── Compact node renderer ─────────────── */
function NodeIcon({ kind, x, y, gold, isDark, pulseClass }) {
  const ringFill = isDark ? "rgba(212,175,55,0.10)" : "#fff";
  return (
    <g className={pulseClass} style={{ transformBox: "fill-box", transformOrigin: `${x}px ${y}px` }}>
      {/* halo */}
      <circle cx={x} cy={y} r="14" fill="url(#vta-glow-sm)" opacity="0.7" />
      {/* ring */}
      <circle cx={x} cy={y} r="10" fill={ringFill} stroke={gold} strokeWidth="1.4" />
      {/* glyph */}
      {kind === "office"  && <OfficeMini  cx={x} cy={y} color={gold} />}
      {kind === "embassy" && <EmbassyMini cx={x} cy={y} color={gold} />}
      {kind === "airport" && <AirportMini cx={x} cy={y} color={gold} />}
      {kind === "client"  && <ClientMini  cx={x} cy={y} color={gold} />}
    </g>
  );
}

/* Office glyph — small VTA monogram badge (the agency's identity) */
function OfficeMini({ cx, cy, color }) {
  return (
    <g>
      {/* roof line above letters to keep an architectural hint */}
      <path
        d={`M ${cx - 5} ${cy - 3.6} L ${cx} ${cy - 5.6} L ${cx + 5} ${cy - 3.6}`}
        stroke={color}
        strokeWidth="0.9"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* VTA monogram */}
      <text
        x={cx}
        y={cy + 2.4}
        textAnchor="middle"
        fontFamily="'Inter', 'Helvetica Neue', Arial, sans-serif"
        fontWeight="900"
        fontSize="6.2"
        letterSpacing="-0.2"
        fill={color}
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        VTA
      </text>
      {/* tiny base line */}
      <line x1={cx - 4} y1={cy + 4.2} x2={cx + 4} y2={cy + 4.2} stroke={color} strokeWidth="0.7" strokeLinecap="round" />
    </g>
  );
}
function EmbassyMini({ cx, cy, color }) {
  return (
    <g stroke={color} strokeWidth="1" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d={`M ${cx - 4.5} ${cy + 4} L ${cx + 4.5} ${cy + 4}`} />
      <path d={`M ${cx - 4} ${cy - 0.5} L ${cx - 4} ${cy + 4} M ${cx - 1.5} ${cy - 0.5} L ${cx - 1.5} ${cy + 4} M ${cx + 1.5} ${cy - 0.5} L ${cx + 1.5} ${cy + 4} M ${cx + 4} ${cy - 0.5} L ${cx + 4} ${cy + 4}`} />
      <path d={`M ${cx - 5} ${cy - 0.5} L ${cx + 5} ${cy - 0.5}`} />
      <path d={`M ${cx - 5} ${cy - 0.5} L ${cx} ${cy - 4} L ${cx + 5} ${cy - 0.5} Z`} fill={color} fillOpacity="0.12" />
    </g>
  );
}
function AirportMini({ cx, cy, color }) {
  // simple airplane silhouette
  return (
    <g fill={color} stroke={color} strokeWidth="0.6" strokeLinejoin="round">
      <path d={`M ${cx - 5} ${cy} L ${cx + 1} ${cy - 1.6} L ${cx + 4.5} ${cy - 3.6} L ${cx + 5.2} ${cy - 3.2} L ${cx + 2.4} ${cy - 0.6} L ${cx + 4} ${cy + 2.4} L ${cx + 3} ${cy + 2.6} L ${cx + 0.4} ${cy + 1} L ${cx - 2} ${cy + 2.4} L ${cx - 2.6} ${cy + 2} L ${cx - 1.4} ${cy + 0.4} Z`} />
    </g>
  );
}
function ClientMini({ cx, cy, color }) {
  // smiley with stamp tick — represents a happy, served client
  return (
    <g stroke={color} strokeWidth="1" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <circle cx={cx} cy={cy - 0.4} r="4.6" fill={color} fillOpacity="0.12" />
      <circle cx={cx - 1.6} cy={cy - 1.2} r="0.55" fill={color} stroke="none" />
      <circle cx={cx + 1.6} cy={cy - 1.2} r="0.55" fill={color} stroke="none" />
      <path d={`M ${cx - 1.8} ${cy + 0.8} Q ${cx} ${cy + 2.4} ${cx + 1.8} ${cy + 0.8}`} />
    </g>
  );
}

/* Country flag glyphs — RU, GB, CA, EU, US (size-parameterised) */
function FlagGlyph({ code, W, H }) {
  if (code === "ru") {
    const band = H / 3;
    return (
      <>
        <rect x="0" y="0"        width={W} height={band} fill="#fff" />
        <rect x="0" y={band}     width={W} height={band} fill="#0039A6" />
        <rect x="0" y={band * 2} width={W} height={band} fill="#D52B1E" />
      </>
    );
  }
  if (code === "gb") {
    // Simplified Union Jack — Blue base + white diagonals + red diagonals + white H/V cross + red H/V cross
    const cx = W / 2, cy = H / 2;
    const tWide = Math.max(2, H * 0.22);  // wide stroke (white)
    const tNarrow = Math.max(1, H * 0.10); // narrow stroke (red)
    return (
      <>
        <rect x="0" y="0" width={W} height={H} fill="#012169" />
        {/* white diagonals */}
        <line x1="0" y1="0" x2={W} y2={H} stroke="#fff" strokeWidth={tWide} />
        <line x1={W} y1="0" x2="0" y2={H} stroke="#fff" strokeWidth={tWide} />
        {/* red diagonals */}
        <line x1="0" y1="0" x2={W} y2={H} stroke="#C8102E" strokeWidth={tNarrow} />
        <line x1={W} y1="0" x2="0" y2={H} stroke="#C8102E" strokeWidth={tNarrow} />
        {/* white H/V cross */}
        <rect x="0" y={cy - tWide / 1.4} width={W} height={tWide * 1.4} fill="#fff" />
        <rect x={cx - tWide / 1.4} y="0" width={tWide * 1.4} height={H} fill="#fff" />
        {/* red H/V cross (slimmer) */}
        <rect x="0" y={cy - tNarrow / 1.2} width={W} height={tNarrow * 1.2} fill="#C8102E" />
        <rect x={cx - tNarrow / 1.2} y="0" width={tNarrow * 1.2} height={H} fill="#C8102E" />
      </>
    );
  }
  if (code === "eu") {
    const cx = W / 2, cy = H / 2, r = Math.min(W, H) * 0.32;
    const stars = [];
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
      stars.push(
        <circle key={i} cx={cx + Math.cos(a) * r} cy={cy + Math.sin(a) * r}
          r={Math.min(W, H) * 0.075} fill="#FFD700" />
      );
    }
    return (<><rect x="0" y="0" width={W} height={H} fill="#003399" />{stars}</>);
  }
  if (code === "us") {
    const stripeH = H / 13;
    const stripes = [];
    for (let i = 0; i < 13; i++) {
      stripes.push(<rect key={i} x="0" y={i * stripeH} width={W} height={stripeH}
        fill={i % 2 === 0 ? "#B22234" : "#fff"} />);
    }
    const cantonW = W * 0.42, cantonH = stripeH * 7;
    const stars = [];
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 4; c++) {
        stars.push(<circle key={`${r}-${c}`}
          cx={(c + 0.6) * (cantonW / 4)}
          cy={(r + 0.6) * (cantonH / 3)}
          r={Math.min(W, H) * 0.05} fill="#fff" />);
      }
    }
    return (<>{stripes}<rect x="0" y="0" width={cantonW} height={cantonH} fill="#3C3B6E" />{stars}</>);
  }
  if (code === "ca") {
    const bandW = W * 0.25;
    const cx = W / 2, cy = H / 2;
    const reach = H * 0.34, inner = H * 0.14;
    const pts = [];
    const points = 11;
    for (let i = 0; i < points * 2; i++) {
      const a = (i / (points * 2)) * Math.PI * 2 - Math.PI / 2;
      const rr = i % 2 === 0 ? reach : inner;
      pts.push(`${cx + Math.cos(a) * rr},${cy + Math.sin(a) * rr}`);
    }
    return (
      <>
        <rect x="0" y="0" width={bandW} height={H} fill="#D52B1E" />
        <rect x={bandW} y="0" width={W - 2 * bandW} height={H} fill="#fff" />
        <rect x={W - bandW} y="0" width={bandW} height={H} fill="#D52B1E" />
        <polygon points={pts.join(" ")} fill="#D52B1E" />
      </>
    );
  }
  return null;
}
